import React, { useEffect, useMemo, useState } from "react";
import ClientLayout from "../../components/layouts/ClientLayout";
import { auth, firestore, database } from "../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import { ref, set, remove } from "firebase/database";
import toast from "react-hot-toast";
import { Settings, Building2, User, Plus, X, Check, Clock } from "lucide-react";
import AddCompanyModal from "./components/AddCompanyModal";

/**
 * ClientSettings Component
 * 
 * Manages client profile and multi-company connections.
 * Unlike farmers (single company), clients can connect to multiple companies.
 * 
 * Firestore user structure for clients:
 *  - connectedCompanies: ["CompanyA", "CompanyB"] (array of company names)
 *  - pendingCompanyRequests: [
 *      {
 *        companyName: "CompanyC",
 *        requestedAt: timestamp,
 *        status: "pending" | "approved" | "declined"
 *      }
 *    ]
 * 
 * Realtime DB structure:
 *  - ClientCompanyRequests/{uid}/{requestId} → client sends request
 *  - Companies/{companyName}/ClientRequests/{uid} → company sees request
 */

const ClientSettings = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [connectedCompanies, setConnectedCompanies] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);

  const requestsMatch = (a, b) =>
    a?.companyName === b?.companyName && a?.requestedAt === b?.requestedAt;

  const findRequestIndex = (requests, target) =>
    requests.findIndex((request) => requestsMatch(request, target));

  const buildRequestKey = (request, index) =>
    request?.requestId ||
    request?.id ||
    `${request?.companyName || "request"}-${request?.requestedAt || "timestamp"}-${index}`;

  // AUTH WATCH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
    });
    return () => unsub();
  }, []);

  // LOAD USER PROFILE
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);

      try {
        if (!user) {
          setProfile(null);
          setConnectedCompanies([]);
          setPendingRequests([]);
          setLoading(false);
          return;
        }

        const userRef = doc(firestore, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.exists() ? snap.data() : null;

        if (!mounted) return;

        setProfile(data || null);
        setConnectedCompanies(data?.connectedCompanies || []);
        setPendingRequests(data?.pendingCompanyRequests || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load profile");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const userIdentifier = useMemo(() => {
    const fullName = [profile?.firstName, profile?.lastName]
      .filter(Boolean)
      .join(" ");
    return (
      profile?.username ||
      fullName ||
      profile?.email ||
      user?.email ||
      user?.uid ||
      "your account"
    );
  }, [profile, user]);

  // ===========================================
  // DISCONNECT FROM COMPANY
  // ===========================================
  const handleDisconnect = async (companyName) => {
    if (!user || !companyName) return false;

    try {
      const uid = user.uid;

      // Remove company from connectedCompanies array
      await updateDoc(doc(firestore, "users", uid), {
        connectedCompanies: arrayRemove(companyName),
      });

      // Notify company about disconnection in RTDB
      const disconnectRef = ref(
        database,
        `Companies/${companyName}/ClientDisconnections/${uid}`
      );
      await set(disconnectRef, {
        uid,
        clientName: [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || profile?.email || "Client",
        requestedAt: new Date().toISOString(),
        status: "requested",
      });

      toast.success(`Disconnected from ${companyName}`);

      // Update UI state
      setConnectedCompanies((prev) => prev.filter((c) => c !== companyName));
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to disconnect");
      return false;
    }
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;

    const normalizedInput = disconnectConfirmation.trim().toLowerCase();
    const expectedValues = [disconnectTarget, userIdentifier]
      .filter(Boolean)
      .map((v) => v.toLowerCase());
    const isValid = expectedValues.some((v) => v === normalizedInput);

    if (!isValid) {
      toast.error("Please type the company name or your username to confirm.");
      return;
    }

    setDisconnecting(true);
    const success = await handleDisconnect(disconnectTarget);
    setDisconnecting(false);

    if (success) {
      setDisconnectTarget(null);
      setDisconnectConfirmation("");
    }
  };

  const openDisconnectModal = (companyName) => {
    setDisconnectTarget(companyName);
    setDisconnectConfirmation("");
  };

  // ===========================================
  // CANCEL PENDING REQUEST
  // ===========================================
  const cancelPendingRequest = async (request) => {
    if (!user || !request) return;

    const requestIndex = findRequestIndex(pendingRequests, request);
    if (requestIndex < 0) {
      console.warn("Pending request not found for cancellation", request);
      return;
    }
    const uid = user.uid;

    try {
      // Remove from RTDB
      await remove(
        ref(database, `ClientCompanyRequests/${uid}/${request.companyName}`)
      );
      
      // Remove from company's pending requests
      await remove(
        ref(database, `Companies/${request.companyName}/ClientRequests/${uid}`)
      );

      // Update Firestore - remove from pendingCompanyRequests array
      const updatedRequests = pendingRequests.filter((_, i) => i !== requestIndex);
      await updateDoc(doc(firestore, "users", uid), {
        pendingCompanyRequests: updatedRequests,
      });

      toast.success(`Request to ${request.companyName} cancelled.`);

      setPendingRequests(updatedRequests);
    } catch (err) {
      console.error(err);
      toast.error("Could not cancel request");
    }
  };

  // =================================================================
  // UI RENDERING
  // =================================================================

  const visiblePendingRequests = useMemo(
    () =>
      pendingRequests.filter(
        (request) => request?.status?.toLowerCase() !== "approved"
      ),
    [pendingRequests]
  );

  if (loading) {
    return (
      <ClientLayout>
        <div className="p-6">Loading profile...</div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* HEADER */}
        <div>
          <h1 className="text-3xl font-bold text-yellow-700 flex items-center gap-2">
            <Settings className="w-8 h-8" />
            Settings
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage your profile and company connections
          </p>
        </div>

        {/* PROFILE CARD */}
        <div className="bg-white p-6 rounded-xl shadow">
          <div className="flex gap-6 items-start">
            <div className="w-24 h-24 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-700 text-3xl font-bold">
              {profile?.firstName
                ? profile.firstName.charAt(0).toUpperCase()
                : profile?.email
                ? profile.email.charAt(0).toUpperCase()
                : "C"}
            </div>

            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-gray-800">
                {profile?.firstName && profile?.lastName
                  ? `${profile.firstName} ${profile.lastName}`
                  : "Client"}
              </h2>
              <p className="text-gray-500 text-sm">{profile?.email}</p>
              
              {profile?.phoneNumber && (
                <p className="text-gray-500 text-sm mt-1">
                  Phone: {profile.phoneNumber}
                </p>
              )}

              {profile?.location && (
                <p className="text-gray-500 text-sm mt-1">
                  Location: {profile.location}
                </p>
              )}

              <div className="mt-4">
                <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  <User className="w-4 h-4 inline mr-1" />
                  Client Account
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CONNECTED COMPANIES SECTION */}
        <div className="bg-white p-6 rounded-xl shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-yellow-700" />
              Connected Companies
            </h3>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
            >
              <Plus className="w-4 h-4" />
              Add Company
            </button>
          </div>

          {connectedCompanies.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">
                You're not connected to any companies yet
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Connect to a Company
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {connectedCompanies.map((companyName, index) => (
                <CompanyCard
                  key={index}
                  companyName={companyName}
                  onDisconnect={openDisconnectModal}
                />
              ))}
            </div>
          )}
        </div>

        {/* PENDING REQUESTS SECTION */}
        {visiblePendingRequests.length > 0 && (
          <div className="bg-white p-6 rounded-xl shadow">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-blue-700" />
              Pending Requests
            </h3>

            <div className="space-y-3">
              {visiblePendingRequests.map((request, index) => (
                <PendingRequestCard
                  key={buildRequestKey(request, index)}
                  request={request}
                  onCancel={() => cancelPendingRequest(request)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ADDITIONAL SETTINGS */}
        <div className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Account Settings
          </h3>
          <p className="text-sm text-gray-600">
            Additional profile settings and preferences will be available here.
          </p>
        </div>
      </div>

      {/* ADD COMPANY MODAL */}
      {showAddModal && (
        <AddCompanyModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          user={user}
          profile={profile}
          connectedCompanies={connectedCompanies}
          onRequestSent={async () => {
            // Refresh profile data
            const snap = await getDoc(doc(firestore, "users", user.uid));
            const data = snap.data();
            setProfile(data);
            setPendingRequests(data?.pendingCompanyRequests || []);
            setShowAddModal(false);
          }}
        />
      )}

      {/* DISCONNECT CONFIRMATION MODAL */}
      {disconnectTarget && (
        <ConfirmDisconnectModal
          companyName={disconnectTarget}
          userIdentifier={userIdentifier}
          confirmationValue={disconnectConfirmation}
          onChangeConfirmation={setDisconnectConfirmation}
          onClose={() => {
            setDisconnectTarget(null);
            setDisconnectConfirmation("");
          }}
          onConfirm={confirmDisconnect}
          loading={disconnecting}
        />
      )}
    </ClientLayout>
  );
};

// =================================================================
// HELPER COMPONENTS
// =================================================================

const CompanyCard = ({ companyName, onDisconnect }) => (
  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-yellow-500 transition">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-700 font-bold">
        {companyName.charAt(0).toUpperCase()}
      </div>
      <div>
        <p className="font-semibold text-gray-800">{companyName}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Check className="w-3 h-3 text-green-600" />
          Connected
        </p>
      </div>
    </div>
    <button
      onClick={() => onDisconnect(companyName)}
      className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition flex items-center gap-1"
    >
      <X className="w-4 h-4" />
      Disconnect
    </button>
  </div>
);

const PendingRequestCard = ({ request, onCancel }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "declined":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
          {request.companyName.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-semibold text-gray-800">{request.companyName}</p>
          <p className="text-xs text-gray-500">
            Requested: {request.requestedAt ? new Date(request.requestedAt).toLocaleDateString() : "—"}
          </p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
            {request.status || "pending"}
          </span>
        </div>
      </div>
      <button
        onClick={onCancel}
        className="px-3 py-1 text-sm border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition"
      >
        Cancel
      </button>
    </div>
  );
};

const ConfirmDisconnectModal = ({
  companyName,
  userIdentifier,
  confirmationValue,
  onChangeConfirmation,
  onClose,
  onConfirm,
  loading,
}) => {
  const expectedLabel = [companyName, userIdentifier].filter(Boolean).join(" / ");
  const normalizedInput = confirmationValue.trim().toLowerCase();
  const expectedValues = [companyName, userIdentifier]
    .filter(Boolean)
    .map((v) => v.toLowerCase());
  const isMatch = expectedValues.some((v) => v === normalizedInput);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">We&apos;re sad to see you go</h3>
          <p className="text-sm text-gray-600 mt-1">
            Leaving <span className="font-semibold text-gray-800">{companyName}</span> means you&apos;ll lose access to their orders and invoices.
            Please type <span className="font-semibold text-gray-800">{expectedLabel}</span> (no pasting) to confirm. Thank you for being with us.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Type exactly: <span className="text-gray-900">{expectedLabel}</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            value={confirmationValue}
            onChange={(e) => onChangeConfirmation(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            placeholder="Enter the value to confirm"
          />
          <p className="text-xs text-gray-500">
            Copy/paste is disabled to be sure this is really you.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Stay Connected
          </button>
          <button
            onClick={onConfirm}
            disabled={!isMatch || loading}
            className={`px-4 py-2 rounded-lg text-white transition ${
              isMatch && !loading
                ? "bg-red-600 hover:bg-red-700"
                : "bg-red-300 cursor-not-allowed"
            }`}
          >
            {loading ? "Disconnecting..." : "Disconnect"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientSettings;
